<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppHeader from './Header.vue'
import AppFooter from './Footer.vue'
import Modal from '@/components/Modal.vue'
import { MENU_ACCOUNT } from '@/constants/menu-account'
import CtaArrowRightIcon from '@/assets/icons/cta-arrow-right.svg?component'
import { useNextOrderStore } from '@/stores/nextOrder'

defineProps<{
  hasBackButton?: boolean
  cartCount: number
  location: string
  isSubscriber: boolean
  hideNavigation?: boolean
}>()

const router = useRouter()
const nextOrderStore = useNextOrderStore()
const showOutOfStock = computed(
  () => nextOrderStore.order?.error_type === 'INSUFFICIENT_INVENTORY',
)

const showAccountMenuMobile = ref(false)
const emit = defineEmits<{
  (e: 'back'): void
  (e: 'goToCart'): void
  (e: 'goToHome'): void
  (e: 'goToUpcomingOrders'): void
  (e: 'goToYourProducts'): void
}>()

const onGoToOutOfStock = () => {
  router.push('/out-of-stock')
}

const onToggleMenuAccountMobile = () => {
  showAccountMenuMobile.value = !showAccountMenuMobile.value
}
</script>

<template>
  <div class="PageTransition">
    <div class="Layout">
      <AppHeader
        :has-back-button="hasBackButton"
        :cart-count="cartCount"
        :location="location"
        :is-subscriber="isSubscriber"
        :hide-navigation="hideNavigation"
        :show-out-of-stock="showOutOfStock"
        @back="emit('back')"
        @go-to-home="emit('goToHome')"
        @go-to-upcoming-orders="emit('goToUpcomingOrders')"
        @go-to-out-of-stock="onGoToOutOfStock"
        @go-to-your-products="emit('goToYourProducts')"
        @go-to-cart="emit('goToCart')"
        @toggle-menu-account-mobile="onToggleMenuAccountMobile"
      />
      <div class="Layout__Container PageTransition__Target">
        <slot></slot>
      </div>
      <AppFooter
        :location="location"
        :is-subscriber="isSubscriber"
        :hide-navigation="hideNavigation"
        :show-out-of-stock="showOutOfStock"
        @go-to-home="emit('goToHome')"
        @go-to-upcoming-orders="emit('goToUpcomingOrders')"
        @go-to-out-of-stock="onGoToOutOfStock"
        @go-to-your-products="emit('goToYourProducts')"
      />
      <Teleport to="#modal-container">
        <div class="ModalAccountMenuMobile">
          <Modal
            v-if="showAccountMenuMobile"
            @close="onToggleMenuAccountMobile"
          >
            <h1 class="ModalAccountMenuMobile__Title">Account</h1>
            <ul class="ModalAccountMenuMobile__Menu">
              <li
                v-for="item in MENU_ACCOUNT.filter(
                  (item) => isSubscriber || item.allowedNonSubscriber,
                )"
                :key="item.label"
                class="ModalAccountMenuMobile__MenuItem"
              >
                <a :href="item.href"
                  >{{ item.label }}
                  <CtaArrowRightIcon width="20" />
                </a>
              </li>
            </ul>
          </Modal>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<style>
.Layout {
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  max-width: var(--container-xxl);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: 100vh;

  @media (--mq-landscape-tablet-and-up) {
    display: block;
  }
}

.PageOverlay {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  z-index: 4;
  visibility: hidden;
  opacity: 0;
  background: var(--color-shadow);
  transition:
    opacity 0.3s ease-in-out,
    visibility 0.3s ease-in-out;
}

.PageOverlay--is-visible {
  overflow: hidden;

  & .PageOverlay {
    opacity: 0.5;
    visibility: visible;
  }
}

.Layout__Container {
  padding-bottom: 100px;
}

.ModalAccountMenuMobile__Title {
  color: var(--color-forest-100);
  font-family: var(--font-family-domaine);
  font-size: 26px;
  font-style: normal;
  font-weight: 400;
  line-height: 36px; /* 138.462% */
}

.ModalAccountMenuMobile__Menu {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.ModalAccountMenuMobile__MenuItem {
  width: 100%;

  a {
    display: flex;
    padding: 6px 0;
    justify-content: space-between;
    color: var(--color-forest-100);
    font-family: var(--font-family-rund);
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px; /* 150% */
  }
}
</style>
